import {ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal} from "@angular/core";
import {ResponsiveDirective} from "../../responsive/responsive.directive";
import {ResponsiveService} from "../../responsive/responsive.service";
import {navParam} from "../../navigation/navigation-params";
import {Page} from "../../navigation/page";
import {OrderingContext, OrderingContextIntention} from '../../ordering-context/ordering-context';
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {StoreData} from "../store-search.model";
import {OrderType} from '../../order/order';
import {ModalPageLayoutComponent} from '../../page-layout/modal-page-layout/modal-page-layout.component';
import {StoreDetailsPanelComponent} from "../store-details-panel/store-details-panel.component";
import {LinkComponent} from '../../buttons/link/link.component';

@Component({
    selector: "woa-store-details-page",
    templateUrl: "./store-details-page.component.html",
    styleUrl: "./store-details-page.component.scss",
    providers: [{provide: Page, useExisting: StoreDetailsPageComponent}],
    imports: [
        LinkComponent,
        ModalPageLayoutComponent,
        ResponsiveDirective,
        StoreDetailsPanelComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StoreDetailsPageComponent extends Page {
    private orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public responsiveService: ResponsiveService = inject(ResponsiveService);

    OrderType = OrderType;

    public readonly orderingContext: Signal<OrderingContext> = navParam.required<OrderingContext>("orderingContext");

    public readonly orderAheadStoreOption: Signal<StoreData> = computed(() =>
        this.orderingContext().selectedStore ? undefined : this.orderingContextModel.singleStoreLocation()?.getStoreForOrderType(OrderType.ORDER_AHEAD));
    public readonly orderDeliveryStoreOption: Signal<StoreData> = computed(() =>
        this.orderingContext().selectedStore ? undefined : this.orderingContextModel.singleStoreLocation()?.getStoreForOrderType(OrderType.ORDER_DELIVERY));
    public readonly orderAtTableStoreOption: Signal<StoreData> = computed(() =>
        this.orderingContext().selectedStore ? undefined : this.orderingContextModel.singleStoreLocation()?.getStoreForOrderType(OrderType.ORDER_AT_TABLE));

    private readonly storeIds: Signal<Set<number>> = computed(() =>
        this.orderingContext().selectedStore ? new Set<number>() : new Set([this.orderAheadStoreOption(), this.orderDeliveryStoreOption(), this.orderAtTableStoreOption()]
            .filter((storeOption: StoreData) => storeOption)
            .map((storeOption: StoreData) => storeOption.storeId)));

    public readonly showStoreOrderTypeOptions: Signal<boolean> = computed(() => this.storeIds().size > 1);

    public readonly intention: Signal<OrderingContextIntention> = computed(() =>
        this.orderingContext().intention == undefined ? OrderingContextIntention.ORDER : this.orderingContext().intention);

    public readonly title: Signal<string> = computed<string>(() =>
        this.orderingContextModel.singleStoreLocation() ? "woa.heading.ordering-context.singleStore"
            : this.intention() === OrderingContextIntention.VIEW_STORES || this.intention() === OrderingContextIntention.SELECT_STORE ? "woa.heading.ordering-context.storeDetails"
                : this.intention() !== OrderingContextIntention.VIEW_CURRENT_STORE ? "woa.heading.ordering-context.closestStore"
                    : this.orderingContextModel.isConfirmedDeliveryOrdering() ? "woa.heading.ordering-context.currentDeliveryStore"
                        : "woa.heading.ordering-context.currentStore");

    public readonly store: WritableSignal<StoreData> = signal<StoreData>(
        this.orderingContext().selectedStore ?? this.orderAheadStoreOption() ?? this.orderDeliveryStoreOption() ?? this.orderAtTableStoreOption());

    public readonly orderType: WritableSignal<OrderType> = signal<OrderType>(
        this.orderingContext().selectedOrderType ?? this.orderingContextModel.orderType() ??
        (!this.showStoreOrderTypeOptions() ? undefined
            : this.orderAheadStoreOption() ? OrderType.ORDER_AHEAD
                : this.orderDeliveryStoreOption() ? OrderType.ORDER_DELIVERY
                    : this.orderAtTableStoreOption() ? OrderType.ORDER_AT_TABLE
                        : undefined));

    public changeOrderType(orderType: OrderType): void {
        this.orderType.set(orderType);
        if (orderType === OrderType.ORDER_AHEAD) {
            this.store.set(this.orderAheadStoreOption());
        } else if (orderType === OrderType.ORDER_DELIVERY) {
            this.store.set(this.orderDeliveryStoreOption());
        } else {
            this.store.set(this.orderAtTableStoreOption());
        }
    }

    public async orderFromStore(orderType: OrderType): Promise<void> {
        void this.dismiss(this.orderingContext()
            .withSelectedStore(this.store())
            .withSelectedOrderType(orderType));
    }
}
